import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const { language } = useLanguage();
  const t = translations[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    // Honeypot check
    if (formData.website_url) {
      // Quietly succeed if bot fills honeypot
      setStatus('success');
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (!response.ok) {
        throw new Error(t.contactForm.error);
      }

      setStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        website_url: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || t.contactForm.error);
    }
  };

  return (
    <div className="bg-white rounded-none border border-bluegray p-6 sm:p-8 md:p-10 shadow-xl">
      <h3 className="font-serif text-2xl font-bold text-navy-900 mb-2">{t.contactForm.title}</h3>
      <p className="text-sm text-navy-900/60 mb-8">
        {language === 'da' ? 'Udfyld formularen nedenfor, så vender René tilbage til dig hurtigst muligt.' : language === 'en' ? 'Fill out the form below, and René will get back to you as soon as possible.' : 'Füllen Sie das untenstehende Formular aus, und René wird sich so schnell wie möglich bei Ihnen melden.'}
      </p>

      {status === 'success' ? (
        <div className="bg-green-accent-light border border-green-accent/20 text-green-accent p-6 rounded-none text-center">
          <i className="fa-solid fa-circle-check text-4xl mb-3"></i>
          <h4 className="font-serif text-lg font-bold mb-1">{t.contactForm.success}</h4>
          <button
            onClick={() => setStatus('idle')}
            className="mt-4 text-xs font-bold uppercase tracking-wider underline hover:text-navy-900 transition-colors"
          >
            {language === 'da' ? 'Send en ny besked' : language === 'en' ? 'Send a new message' : 'Neue Nachricht senden'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field (absolutely hidden from humans) */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-xs font-bold uppercase tracking-widest text-navy-900/80 mb-2">
                {t.contactForm.name} <span className="text-petrol">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={language === 'da' ? 'Dit fulde navn' : language === 'en' ? 'Your full name' : 'Ihr vollständiger Name'}
                className="w-full bg-offwhite border border-bluegray rounded-none px-4 py-3 text-sm text-navy-900 placeholder-navy-900/40 focus:outline-none focus:ring-2 focus:ring-petrol/20 focus:border-petrol transition-all"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-widest text-navy-900/80 mb-2">
                {t.contactForm.phone} <span className="text-petrol">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder={language === 'da' ? 'F.eks. 30 65 55 09' : language === 'en' ? 'E.g. +45 30 65 55 09' : 'Z.B. +45 30 65 55 09'}
                className="w-full bg-offwhite border border-bluegray rounded-none px-4 py-3 text-sm text-navy-900 placeholder-navy-900/40 focus:outline-none focus:ring-2 focus:ring-petrol/20 focus:border-petrol transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-navy-900/80 mb-2">
              {t.contactForm.email} <span className="text-petrol">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder={language === 'da' ? 'din@email.dk' : language === 'en' ? 'your@email.com' : 'ihre@email.de'}
              className="w-full bg-offwhite border border-bluegray rounded-none px-4 py-3 text-sm text-navy-900 placeholder-navy-900/40 focus:outline-none focus:ring-2 focus:ring-petrol/20 focus:border-petrol transition-all"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-xs font-bold uppercase tracking-widest text-navy-900/80 mb-2">
              {t.contactForm.message} <span className="text-petrol">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="5"
              value={formData.description}
              onChange={handleChange}
              placeholder={language === 'da' ? 'Beskriv dit projekt eller din forespørgsel...' : language === 'en' ? 'Describe your project or inquiry...' : 'Beschreiben Sie Ihr Projekt oder Ihre Anfrage...'}
              className="w-full bg-offwhite border border-bluegray rounded-none px-4 py-3 text-sm text-navy-900 placeholder-navy-900/40 focus:outline-none focus:ring-2 focus:ring-petrol/20 focus:border-petrol transition-all resize-none"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-none text-sm flex items-center gap-2.5">
              <i className="fa-solid fa-circle-exclamation text-base"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full bg-navy-900 hover:bg-petrol text-offwhite py-4 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {status === 'loading' ? (
              <>
                <i className="fa-solid fa-spinner animate-spin"></i>
                <span>{t.contactForm.sending}</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i>
                <span>{t.contactForm.send}</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
